import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useMutation, useQueryClient } from 'react-query'
import update from 'immutability-helper'
import arraySort from 'array-sort'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import styled from 'styled-components'
import colors from '../../../configuration/colors'
import withApiData from '../../HOC/withApiData'
import { hideModal } from '../../../actions/modal'
import Modal from '../../mini/Modal'
import Button from '../../mini/Buttons/Button'
import CancelButton from '../../mini/Buttons/CancelButton'
import H2 from '../../typography/headers/H2'
import MarginRight15 from '../../typography/margins/MarginRight15'
import { reorderJourneyLevels } from '../../mycourses/Levels/API'
import { DraggableLevelItem } from './DraggableLevelItem'

const ModalContainer = styled.div`
  width: 660px;
  margin: 100px auto;
  border-radius: 10px;
  box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.08);
  background-color: ${colors.smoke};
`

const HeaderContainer = styled.div`
  height: 80px;
  width: auto;
  border-radius: 10px 10px 0 0;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.rock};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
`

const MainContainer = styled.div`
  padding: 30px 30px 32px 30px;
`

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const SortLevelOrderModal = ({
  apiData,
  activityCourseId,
  journeyId,
  levels
}) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const handleCloseModal = () => {
    dispatch(hideModal())
  }

  const [levelsState, setLevelsState] = useState(levels)

  useEffect(() => {
    if (levelsState !== levels) {
      setLevelsState(arraySort(levels, 'sort_order'))
    }
  }, [levels])

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      const levelsCopy = [...levelsState]
      const dragCard = levelsCopy[dragIndex]
      setLevelsState(
        update(levelsCopy, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard]
          ]
        })
      )
    },
    [levelsState]
  )

  const reorderLevels = useMutation(
    ({ apiData, activityCourseId, journeyId, levelsIdsAndSortOrder }) =>
      reorderJourneyLevels(
        apiData,
        activityCourseId,
        journeyId,
        levelsIdsAndSortOrder
      )
  )

  const handleSaveEditedCourse = () => {
    let levelsIdsAndSortOrder = []
    levelsState.map((l, index) =>
      levelsIdsAndSortOrder.push({ level_id: l.id, sort_order_position: index })
    )

    reorderLevels.mutate(
      {
        apiData,
        activityCourseId,
        journeyId,
        levelsIdsAndSortOrder
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries('journey-levels-' + journeyId)
          setTimeout(() => handleCloseModal(), 300)
        }
      }
    )
  }

  const listOfLevels = levelsState.map((level, index) => {
    return (
      <DraggableLevelItem
        key={level.id}
        level={level}
        id={level.id}
        index={index}
        moveCard={moveCard}
        activityCourseId={activityCourseId}
        journeyId={journeyId}
        levels={levelsState}
      />
    )
  })

  return (
    <Modal
      width={1400}
      percentHeight={100}
      background={'rgba(36,54,70,0.48)'}
      lockBodyScroll={false}
      opacity={'.9'}
      allowInnerScroll
      allowCloseOnExternalClick={false}
    >
      <ModalContainer>
        <HeaderContainer>
          <H2>Level Sort Order</H2>
          <FlexContainer>
            <MarginRight15>
              <CancelButton
                handleClick={handleCloseModal}
                title="Cancel"
                width="104px"
              />
            </MarginRight15>
            <div
              data-for="editCourseTooltip"
              data-tip="Please enter all required fields prior to creating this course."
            >
              <Button
                width="104px"
                title="Save"
                handleClick={handleSaveEditedCourse}
                buttonColor={colors.oeYellow}
                borderColor={colors.oeYellow}
                hoverBackground={colors.charcoal4}
                hoverColor={colors.white}
                shouldShowTooltipAndDisableButton={false}
              />
            </div>
          </FlexContainer>
        </HeaderContainer>
        <DndProvider backend={HTML5Backend}>
          <MainContainer>{listOfLevels}</MainContainer>
        </DndProvider>
      </ModalContainer>
    </Modal>
  )
}

SortLevelOrderModal.propTypes = {
  apiData: PropTypes.object.isRequired,
  activityCourseId: PropTypes.number.isRequired,
  journeyId: PropTypes.number.isRequired,
  levels: PropTypes.array.isRequired
}

export default withApiData(SortLevelOrderModal)



// WEBPACK FOOTER //
// ./src/components/modal/SortLevelOrder/SortLevelOrderModal.js